// src/components/Admin/Dashboard.jsx
import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/SidebarAdmin";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  ButtonGroup,
  Button,
  Stack,
} from "@mui/material";
import { ref, get, child } from "firebase/database";
import { database } from "../../firebase";

// Import the new Graphs component
import Graphs from './graphs';

const Dashboard = () => {
  // ─── Sidebar state ───────────────────────────────────────────────────────
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const sidebarWidth = isSidebarOpen ? 250 : 80;

  // ─── Users state ────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [uLoading, setULoading] = useState(true);
  const [uError, setUError] = useState("");

  // ─── Coinbox state ──────────────────────────────────────────────────────
  const [coinbox, setCoinbox] = useState([]);
  const [cLoading, setCLoading] = useState(true);
  const [cError, setCError] = useState("");

  // ─── Display Mode state ──────────────────────────────────────────────────
  const [displayMode, setDisplayMode] = useState("userStats"); // 'userStats', 'coinboxStats'

  // ─── Fetch all users ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snap = await get(child(ref(database), "users"));
        if (snap.exists()) {
          const usersData = Object.values(snap.val());
          setUsers(usersData);
        } else {
          setUsers([]);
        }
      } catch (error) {
         console.error("Error fetching users:", error);
        setUError("Failed to fetch users. Please try again.");
        setUsers([]);
      } finally {
        setULoading(false);
      }
    };
    fetchUsers();
  }, []); // Empty dependency array means this runs once on mount

  // ─── Fetch coinbox data from root coinbox/lockers ────────────────────────
  useEffect(() => {
    const fetchCoinbox = async () => {
      try {
        const snap = await get(ref(database, "coinbox/lockers"));
        if (snap.exists()) {
          const coinboxData = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
          setCoinbox(coinboxData);
        } else {
          setCoinbox([]);
        }
      } catch (error) {
         console.error("Error fetching coinbox data:", error);
        setCError("Failed to load coinbox data. Please try again.");
         setCoinbox([]);
      } finally {
        setCLoading(false);
      }
    };
    fetchCoinbox();
  }, []); // Empty dependency array means this runs once on mount


  return (
    <Box sx={{ backgroundColor: "#f7f9fc", minHeight: "100vh", pt: 8, display: 'flex' }}>
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main content wrapper with sidebar offset */}
      <Box
        component="main"
        sx={{
          marginLeft: { xs: 0, sm: `${sidebarWidth}px` },
          transition: "margin-left 0.3s ease",
          width: { xs: "100%", sm: `calc(100% - ${sidebarWidth}px)` },
          p: { xs: 2, sm: 3 },
          flexGrow: 1,
        }}
      >
        {/* Dashboard title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            mb: 3,
            color: "primary.main",
            textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          Admin Dashboard
        </Typography>

        {/* Main Grid Container */}
        <Grid container spacing={3}>

            {/* View Switcher Buttons */}
            <Grid item xs={12} sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
                     <Typography variant="h6" sx={{ mr: {sm: 1} }}>Show:</Typography>
                    <ButtonGroup variant="contained" aria-label="dashboard view switcher buttons">
                        <Button
                            onClick={() => setDisplayMode("userStats")}
                            // Disable if users are loading AND no users were previously loaded
                            disabled={uLoading && users.length === 0}
                            color={displayMode === "userStats" ? "primary" : "inherit"}
                            variant={displayMode === "userStats" ? "contained" : "outlined"}
                        >
                            User Statistics
                        </Button>
                        <Button
                            onClick={() => setDisplayMode("coinboxStats")}
                             // Disable if coinbox data is loading AND no coinbox data was previously loaded
                            disabled={cLoading && coinbox.length === 0}
                             color={displayMode === "coinboxStats" ? "primary" : "inherit"}
                             variant={displayMode === "coinboxStats" ? "contained" : "outlined"}
                        >
                            Coinbox Statistics
                        </Button>
                    </ButtonGroup>
                </Stack>
            </Grid>

            {/* Render Graphs component - it will display the correct graphs based on displayMode */}
             <Graphs
                 users={users}
                 uLoading={uLoading}
                 uError={uError}
                 coinbox={coinbox}
                 cLoading={cLoading}
                 cError={cError}
                 displayMode={displayMode}
             />

        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;